import { types } from 'mobx-state-tree';
import { GridRowParams, GridValidRowModel } from '@mui/x-data-grid';
import { DateTime } from 'luxon';
import {
  isRollingDateRange,
  isSerializedDateRange,
  resolveDateRange,
  serializeDateRange,
  type V5ResolvedDateRange,
  type V5SerializedDateRange,
} from '@routific/hive/v5';
import getAppRoot from './utils/getAppRoot';
import { OrderStatusFilter, VisitDtoModelType } from './models';
import { DateRangeInput, VisitFilterInput } from './models/RootStore.base';
import { LassoMode } from '../../components/map/constants';
import { FiltersType } from '../../hooks/useFilters';
import { ORDERS_DEFAULT_SEARCH_RANGE_DAYS } from '../../constants';

const defaultFilter = {
  active: {},
  config: {
    isUnscheduled: true,
  },
} as any;

// A calendar-day range (YYYY-MM-DD); either bound may be open.
export interface OrdersPageDateRange {
  start?: string;
  end?: string;
}

// A committed order-filter selection, applied in one shot from the FilterSearch bar.
export interface OrderFilterSelection {
  status?: OrderStatusFilter[];
  scheduledDateRange?: OrdersPageDateRange;
  deliveryDateRange?: OrdersPageDateRange;
  hasNotes?: boolean;
  addressIssues?: boolean;
  customerNames?: string[];
}

// Created-date range. Sent as the top-level createdFrom/createdTo query args (ISO datetimes),
// separate from the structured `filter` input; it always has a default (30-day) window and never clears.
export type OrdersPageSearchDateRange = {
  createdFrom: string;
  createdTo: string;
};

/** Calendar-day bounds stored as YYYY-MM-DD (local). */
export type OrdersPageCreatedDateBounds = {
  fromDate: string;
  toDate: string;
};

export const getDefaultOrdersPageCreatedDateBounds = (): OrdersPageCreatedDateBounds => {
  const end = DateTime.now().startOf('day');
  const start = end.minus({ days: ORDERS_DEFAULT_SEARCH_RANGE_DAYS });

  return {
    fromDate: start.toISODate() as string,
    toDate: end.toISODate() as string,
  };
};

export const toOrdersPageSearchDateRange = (
  bounds: OrdersPageCreatedDateBounds | undefined | null,
): OrdersPageSearchDateRange | undefined => {
  if (bounds?.fromDate == null || bounds?.toDate == null) {
    return undefined;
  }

  const from = DateTime.fromISO(bounds.fromDate).startOf('day');
  const to = DateTime.fromISO(bounds.toDate).endOf('day');
  if (!from.isValid || !to.isValid) {
    return undefined;
  }

  const createdFrom = from.toUTC().toISO();
  const createdTo = to.toUTC().toISO();
  if (createdFrom == null || createdTo == null) {
    return undefined;
  }

  return { createdFrom, createdTo };
};

const defaultCreatedDateBounds = getDefaultOrdersPageCreatedDateBounds();

// The committed order-filter selection is persisted here so a page refresh restores the
// user's last filters. The stored object mirrors the `filter` (VisitFilterInput) sent to the
// visitsPaginated query — same field names and nested `{ start, end }` ranges — so the two read the
// same. Only the fields the orders page drives are stored (not the draft/search UI).
const ORDERS_FILTERS_STORAGE_KEY = 'ordersPage.filters';

// A subset of VisitFilterInput: the order-filter groups the orders page owns. Created date uses Hive's
// serialized shape so ranges ending today can be re-anchored when restored. `createdDateRange` remains
// readable for users with the previous fixed-range storage format.
type PersistedOrdersFilters = Pick<
VisitFilterInput,
'status' | 'scheduledDateRange' | 'deliveryDateRange' | 'driverNotes' | 'addressIssues' | 'customerNames' | 'createdDateRange'
> & {
  createdDate?: V5SerializedDateRange;
};

const readPersistedOrdersFilters = (): PersistedOrdersFilters => {
  if (globalThis.window?.localStorage === undefined) return {};

  try {
    const raw = globalThis.window.localStorage.getItem(ORDERS_FILTERS_STORAGE_KEY);
    if (!raw) return {};

    const parsed = JSON.parse(raw) as unknown;
    return parsed != null && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
};

// Merge a partial update into the stored selection so writing one filter group (e.g. the created-date
// range) never clobbers the others. `undefined` fields are dropped by JSON.stringify (i.e. left unset).
const writePersistedOrdersFilters = (patch: PersistedOrdersFilters) => {
  if (globalThis.window?.localStorage === undefined) return;

  try {
    const next = { ...readPersistedOrdersFilters(), ...patch };
    globalThis.window.localStorage.setItem(ORDERS_FILTERS_STORAGE_KEY, JSON.stringify(next));
  } catch {
    // Ignore write failures (quota exceeded / storage disabled) — persistence is best-effort.
  }
};

// A YYYY-MM-DD range for the persisted VisitFilterInput, or undefined when both bounds are unset (so an
// empty range is omitted, exactly as the `orderFilter` view omits it from the query).
const toPersistedDateRange = (start?: string, end?: string): DateRangeInput | undefined =>
  (start || end ? { start, end } : undefined);

const toCreatedDateBounds = (
  [from, to]: V5ResolvedDateRange,
): OrdersPageCreatedDateBounds | undefined => {
  const fromDate = from.toISODate();
  const toDate = to.toISODate();

  return from.isValid && to.isValid && fromDate != null && toDate != null
    ? { fromDate, toDate }
    : undefined;
};

const getPersistedCreatedDateBounds = (
  persisted: PersistedOrdersFilters,
): OrdersPageCreatedDateBounds | undefined => {
  if (isSerializedDateRange(persisted.createdDate)) {
    const bounds = toCreatedDateBounds(resolveDateRange(persisted.createdDate));
    if (bounds != null) return bounds;
  }

  const fromDate = persisted.createdDateRange?.start;
  const toDate = persisted.createdDateRange?.end;
  return fromDate != null && toDate != null ? { fromDate, toDate } : undefined;
};

// Seed snapshot for the OrdersPage store from the persisted VisitFilterInput. Omitted created-date bounds
// fall back to the model's rolling 30-day default (types.optional), so untouched date filters stay current.
export const getInitialOrdersPageState = () => {
  const persisted = readPersistedOrdersFilters();
  const createdDateBounds = getPersistedCreatedDateBounds(persisted);

  return {
    orderStatusFilter: persisted.status ?? [],
    scheduledFromDate: persisted.scheduledDateRange?.start ?? undefined,
    scheduledToDate: persisted.scheduledDateRange?.end ?? undefined,
    deliveryFromDate: persisted.deliveryDateRange?.start ?? undefined,
    deliveryToDate: persisted.deliveryDateRange?.end ?? undefined,
    hasNotesFilter: persisted.driverNotes ?? undefined,
    addressIssuesFilter: persisted.addressIssues ?? undefined,
    customerNamesFilter: persisted.customerNames ?? [],
    createdFromDate: createdDateBounds?.fromDate,
    createdToDate: createdDateBounds?.toDate,
  };
};

const OrdersPage = types
  .model('OrdersPage').props({
    queriedVisitIds: types.array(types.string),
    selectedOrderIds: types.optional(types.array(types.string), []),
    // Map lasso state lives here (not in the OrdersMap hook) because sibling surfaces depend on it:
    // the table locks its row checkboxes while a lasso is active, and clearing the selection must also
    // dismiss the drawn polygon. The map hook still owns the ephemeral polygon geometry.
    lassoMode: types.maybeNull(types.enumeration<LassoMode>('LassoMode', Object.values(LassoMode))),
    filters: types.optional(types.frozen<FiltersType<VisitDtoModelType>>(), defaultFilter),
    searchTerm: types.optional(types.string, ''),
    // Server-side order filters, assembled into the visitsPaginated `filter` input by the
    // `orderFilter` view. They are edited through the Hive `FilterSearch` bar, which owns its own
    // draft/apply UI (inline Status + a "More filters" drawer), so the store keeps only the committed,
    // query-driving values.
    orderStatusFilter: types.array(types.string),
    // Scheduled / delivery date ranges (YYYY-MM-DD bounds; either may be open). Backed by
    // VisitFilterInput.scheduledDateRange / deliveryDateRange.
    scheduledFromDate: types.maybe(types.string),
    scheduledToDate: types.maybe(types.string),
    deliveryFromDate: types.maybe(types.string),
    deliveryToDate: types.maybe(types.string),
    hasNotesFilter: types.maybe(types.boolean),
    // Combined address-issues toggle (invalid OR questionable); backed by VisitFilterInput.addressIssues.
    addressIssuesFilter: types.maybe(types.boolean),
    customerNamesFilter: types.array(types.string),
    // Created-date range — serializable calendar days (avoid storing Luxon DateTime in MST).
    createdFromDate: types.optional(types.string, defaultCreatedDateBounds.fromDate),
    createdToDate: types.optional(types.string, defaultCreatedDateBounds.toDate),
  })
  .actions(self => ({
    setSearchTerm(term: string) {
      self.searchTerm = term;
    },
    // Commit a created-date range. No-op if unchanged so it doesn't churn the table refetch.
    setCreatedDateBounds(bounds: OrdersPageCreatedDateBounds) {
      if (self.createdFromDate === bounds.fromDate && self.createdToDate === bounds.toDate) {
        return;
      }

      const serialized = serializeDateRange([
        DateTime.fromISO(bounds.fromDate),
        DateTime.fromISO(bounds.toDate),
      ]);
      writePersistedOrdersFilters(
        isRollingDateRange(serialized)
          ? { createdDate: serialized, createdDateRange: undefined }
          : { createdDate: undefined, createdDateRange: serialized },
      );

      self.createdFromDate = bounds.fromDate;
      self.createdToDate = bounds.toDate;
    },
    // Seed or refresh the rolling window when the Orders page mounts.
    ensureDefaultDateRange() {
      const persisted = readPersistedOrdersFilters();
      let rollingBounds: OrdersPageCreatedDateBounds | undefined;

      if (isRollingDateRange(persisted.createdDate)) {
        rollingBounds = toCreatedDateBounds(resolveDateRange(persisted.createdDate));
      } else if (
        !isSerializedDateRange(persisted.createdDate)
        && persisted.createdDateRange == null
      ) {
        rollingBounds = getDefaultOrdersPageCreatedDateBounds();
      }

      if (rollingBounds != null) {
        self.createdFromDate = rollingBounds.fromDate;
        self.createdToDate = rollingBounds.toDate;
        return;
      }

      if (self.createdFromDate && self.createdToDate) {
        return;
      }
      const defaults = getDefaultOrdersPageCreatedDateBounds();
      self.createdFromDate = defaults.fromDate;
      self.createdToDate = defaults.toDate;
    },
    setQueriedVisitIds(ids: string[]) {
      self.queriedVisitIds.clear();
      self.queriedVisitIds.push(...ids);
    },
    // Commit a whole filter selection from the FilterSearch bar (or the address-issues toggle).
    // Changing `orderFilter` refetches the table via filterKey. An empty selection
    // (FilterSearch "Clear all") clears every category.
    applyOrderFilters(selection: OrderFilterSelection) {
      self.orderStatusFilter.replace(selection.status ?? []);
      self.scheduledFromDate = selection.scheduledDateRange?.start;
      self.scheduledToDate = selection.scheduledDateRange?.end;
      self.deliveryFromDate = selection.deliveryDateRange?.start;
      self.deliveryToDate = selection.deliveryDateRange?.end;
      self.hasNotesFilter = selection.hasNotes;
      self.addressIssuesFilter = selection.addressIssues;
      self.customerNamesFilter.replace(selection.customerNames ?? []);
      // Persist the committed selection so a refresh restores it — shaped like the query's VisitFilterInput
      // (empty groups omitted). Merges, so it won't clobber the separately-committed created-date range.
      writePersistedOrdersFilters({
        status: self.orderStatusFilter.length ? (self.orderStatusFilter.slice() as OrderStatusFilter[]) : undefined,
        scheduledDateRange: toPersistedDateRange(self.scheduledFromDate, self.scheduledToDate),
        deliveryDateRange: toPersistedDateRange(self.deliveryFromDate, self.deliveryToDate),
        driverNotes: self.hasNotesFilter,
        addressIssues: self.addressIssuesFilter,
        customerNames: self.customerNamesFilter.length ? self.customerNamesFilter.slice() : undefined,
      });
    },
    // Drawer Clear all: empty every order-filter group and force Created Date back to the
    // rolling default 30-day window (setCreatedDateBounds — ensureDefaultDateRange is a no-op
    // once a range is already set).
    clearAllFilters() {
      self.applyOrderFilters({});
      self.setCreatedDateBounds(getDefaultOrdersPageCreatedDateBounds());
    },
    // Multi-select on the orders page.
    setSelectedOrderIds(ids: string[]) {
      self.selectedOrderIds.clear();
      self.selectedOrderIds.push(...ids);
    },
    clearSelectedOrders() {
      self.selectedOrderIds.clear();
      // Clearing the selection also exits the lasso so a cleared selection never leaves a stale
      // polygon behind on the map (the OrdersMap hook resets its geometry when lassoMode goes null).
      self.lassoMode = null;
    },
    // Drop only the given ids from the selection, leaving the rest of an in-progress multi-select
    // intact. Used when a delete event fires: we must forget the deleted ids (the grid's
    // keepNonExistentRowsSelected would otherwise keep them "selected"), but an unrelated/background
    // delete shouldn't wipe a selection the user is still assembling.
    removeSelectedOrders(ids: string[]) {
      if (!ids.length || !self.selectedOrderIds.length) return;
      const toRemove = new Set(ids);
      const remaining = self.selectedOrderIds.filter((id) => !toRemove.has(id));
      if (remaining.length !== self.selectedOrderIds.length) {
        self.selectedOrderIds.replace(remaining);
      }
    },
    setLassoMode(mode: LassoMode | null) {
      self.lassoMode = mode;
    },
    selectOrder(row: GridRowParams) {
      getAppRoot(self).ui.selectVisit(row?.row?.id);
    },
  }))
  .actions(self => ({
    // Footer / header address-issues toggle. Preserves every other committed category.
    setAddressIssuesFilter(value?: boolean) {
      if (self.addressIssuesFilter === value) return;
      self.applyOrderFilters({
        status: self.orderStatusFilter.length ? (self.orderStatusFilter.slice() as OrderStatusFilter[]) : undefined,
        scheduledDateRange: (self.scheduledFromDate || self.scheduledToDate)
          ? { start: self.scheduledFromDate, end: self.scheduledToDate }
          : undefined,
        deliveryDateRange: (self.deliveryFromDate || self.deliveryToDate)
          ? { start: self.deliveryFromDate, end: self.deliveryToDate }
          : undefined,
        hasNotes: self.hasNotesFilter,
        addressIssues: value,
        customerNames: self.customerNamesFilter.length ? self.customerNamesFilter.slice() : undefined,
      });
    },
  }))
  .views(self => ({
    get orders(): GridValidRowModel[] {
      return self.queriedVisitIds
        .map(id => getAppRoot(self).model.visitDtos?.get(id))
        .filter(row => row !== undefined) as GridValidRowModel[];
    },
    // Assembled filter input for the query, from committed fields only; undefined fields are omitted
    // (no-op) server-side. Delivery-date precedence over the top-level query arg is handled in
    // usePaginatedOrders.
    get orderFilter(): VisitFilterInput {
      return {
        status: self.orderStatusFilter.length ? (self.orderStatusFilter.slice() as OrderStatusFilter[]) : undefined,
        // Scheduled / delivery date ranges (YYYY-MM-DD bounds); omitted when both bounds are unset.
        scheduledDateRange: (self.scheduledFromDate || self.scheduledToDate)
          ? { start: self.scheduledFromDate, end: self.scheduledToDate }
          : undefined,
        deliveryDateRange: (self.deliveryFromDate || self.deliveryToDate)
          ? { start: self.deliveryFromDate, end: self.deliveryToDate }
          : undefined,
        // Internal committed state stays `hasNotesFilter`; the wire field is `filter.driverNotes`.
        driverNotes: self.hasNotesFilter,
        // Combined invalid-OR-questionable address filter (backend collapses both into one boolean).
        addressIssues: self.addressIssuesFilter,
        customerNames: self.customerNamesFilter.length ? self.customerNamesFilter.slice() : undefined,
        // Mirror the top-level createdFrom/createdTo range inside `filter` (a copy), so it also
        // rides the structured filter path. Uses the same UTC-ISO bounds as the top-level args.
        createdDateRange: this.searchDateRange
          ? { start: this.searchDateRange.createdFrom, end: this.searchDateRange.createdTo }
          : undefined,
      };
    },
    // Count of active committed categories — "any filter applied" for surfaces that need it.
    get appliedFilterCount(): number {
      return (self.orderStatusFilter.length > 0 ? 1 : 0)
        + (self.scheduledFromDate != null || self.scheduledToDate != null ? 1 : 0)
        + (self.deliveryFromDate != null || self.deliveryToDate != null ? 1 : 0)
        + (self.hasNotesFilter != null ? 1 : 0)
        + (self.addressIssuesFilter != null ? 1 : 0)
        + (self.customerNamesFilter.length > 0 ? 1 : 0);
    },
    get hasActiveOrderFilters(): boolean {
      return this.appliedFilterCount > 0;
    },
    // Created-date range.
    get createdDateBounds(): OrdersPageCreatedDateBounds {
      return {
        fromDate: self.createdFromDate,
        toDate: self.createdToDate,
      };
    },
    // The created-date range as top-level createdFrom/createdTo query args (UTC ISO), or undefined if unset.
    get searchDateRange(): OrdersPageSearchDateRange | undefined {
      if (!self.createdFromDate || !self.createdToDate) {
        return undefined;
      }

      return toOrdersPageSearchDateRange({
        fromDate: self.createdFromDate,
        toDate: self.createdToDate,
      });
    },
    // Multi-select on the orders page.
    get selectedOrders(): VisitDtoModelType[] {
      return self.selectedOrderIds
        .map(id => getAppRoot(self).model.visitDtos?.get(id))
        .filter(order => order !== undefined) as VisitDtoModelType[];
    },
    // Whether the map lasso is active (drawing or editing). Read by the table to lock row selection.
    get isLassoMode(): boolean {
      return !!self.lassoMode;
    },
  }))
  .views(self => ({
    get selectedOrderCount(): number {
      return self.selectedOrders.length;
    },
  }));

export default OrdersPage;
